import { FormEvent, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, MessageCircle, Send, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { isPurchasingManagerEnabled } from '@/lib/organizationFeatures';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  asOf?: string;
}

interface FallbackRecommendation {
  item_name: string;
  location_name: string;
  recommended_quantity: number | string;
  unit_of_measure: string;
  priority: 'urgent' | 'high' | 'overstock' | 'normal';
  reason: string;
  confidence: 'low' | 'medium' | 'high';
}

interface StaffLoginInvestigation {
  staff_id: string;
  staff_name: string;
  login_count: number | string;
  first_login_at: string | null;
  last_login_at: string | null;
  evidence: {
    source?: string;
    action?: string;
    date_range?: { start_at?: string; end_at?: string };
  };
}

interface CashierActivityInvestigation {
  staff_id: string;
  staff_name: string;
  audit_event_count: number | string;
  audit_actions: Record<string, number | string>;
  login_count: number | string;
  orders_created: number | string;
  payments_count: number | string | null;
  payments_amount: number | string | null;
  refunds_count: number | string | null;
  refunds_amount: number | string | null;
  register_sessions_opened: number | string;
  cash_drawer_transaction_count: number | string;
  financial_metrics_available: boolean;
  first_activity_at: string | null;
  last_activity_at: string | null;
  evidence: {
    date_range?: { start_at?: string; end_at?: string };
  };
}

interface SalesProductRanking {
  product_name: string;
  sku: string | null;
  units_sold: number | string;
  net_sales: number | string;
  order_count: number | string;
  location_count: number | string;
}

interface AiAssistantSettings {
  provider_mode: 'platform_openai' | 'customer_openai' | 'customer_anthropic';
  model: string;
  customer_key_configured: boolean;
  customer_key_last4: string | null;
  onboarding_completed_at: string | null;
  can_configure: boolean;
}

const STARTER_QUESTIONS = [
  'What should I buy this week?',
  'How much should I buy to last until next week Friday?',
  'How much of a raw material is in stock, and what should I buy next week?',
  'Which items are at risk of stocking out?',
  'What changed in supplier spend?',
];

const STAFF_LOGIN_PATTERN = /\b(log(?:ged)?\s*in|login|sign(?:ed)?\s*in)\b/i;
const CASHIER_ACTIVITY_PATTERN = /\b(cashier|staff|employee|team member|user)\b/i;
const CASHIER_ACTIVITY_TERMS = /\b(activity|activities|actions?|pos|register|sales?|sold|sell|voids?|refunds?|discounts?|payments?|performance|worked|work|did)\b/i;

function isStaffLoginInvestigation(question: string) {
  return STAFF_LOGIN_PATTERN.test(question) && /\b(staff|employee|team member|user|did)\b/i.test(question);
}

function getStaffNameFromQuestion(question: string) {
  const patterns = [
    /\b(?:staff|employee|team member|user)\s+(.+?)\s+(?:log(?:ged)?\s*in|login|sign(?:ed)?\s*in)\b/i,
    /\bdid\s+(.+?)\s+(?:log(?:ged)?\s*in|login|sign(?:ed)?\s*in)\b/i,
  ];
  const match = patterns.map((pattern) => question.match(pattern)).find(Boolean);
  return match?.[1]?.replace(/\b(?:to|into)\s+(?:the\s+)?system\b.*$/i, '').replace(/[?.!,]+$/g, '').trim() || null;
}

function isCashierActivityInvestigation(question: string) {
  return CASHIER_ACTIVITY_PATTERN.test(question) && CASHIER_ACTIVITY_TERMS.test(question) && !isStaffLoginInvestigation(question);
}

function isSalesQuestion(question: string) {
  return /\b(sales?|revenue|most\s+sold|best\s+selling|top\s+selling|least\s+sold|products?\s+sold|units?\s+sold)\b/i.test(question);
}

function getQuestionDateRange(question: string) {
  const endAt = new Date();
  const startAt = new Date(endAt);
  if (/\b(this year|year to date|ytd)\b/i.test(question)) {
    startAt.setMonth(0, 1);
    startAt.setHours(0, 0, 0, 0);
  } else if (/\blast\s+month\w*\b/i.test(question)) {
    startAt.setMonth(startAt.getMonth() - 1, 1);
    startAt.setHours(0, 0, 0, 0);
    endAt.setDate(0);
    endAt.setHours(23, 59, 59, 999);
  } else if (/\bthis\s+month\w*\b/i.test(question)) {
    startAt.setDate(1);
    startAt.setHours(0, 0, 0, 0);
  } else {
    const dayMatch = question.match(/\b(?:last|past)\s+(\d{1,3})\s+days?\b/i);
    startAt.setDate(startAt.getDate() - (dayMatch ? Math.min(Math.max(Number(dayMatch[1]), 1), 366) : 30));
  }
  return { startAt, endAt };
}

function getStaffNameFromActivityQuestion(question: string) {
  const directMatch = question.match(/\b(?:cashier|staff|employee|team member|user)\s+(.+)/i);
  if (!directMatch?.[1]) return null;
  return directMatch[1]
    .replace(/[’']s\b/i, '')
    .replace(/\s+\b(?:pos|register|activity|activities|actions?|sales?|sold|sell|voids?|refunds?|discounts?|payments?|performance|worked|work|did|have|has)\b.*$/i, '')
    .replace(/[?.!,]+$/g, '')
    .trim() || null;
}

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleString() : 'not recorded';
}

function formatAmount(value: number | string) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

async function functionErrorMessage(error: unknown, fallback: string) {
  const response = (error as { context?: unknown } | null)?.context;
  if (response instanceof Response) {
    try {
      const body = await response.clone().json() as { error?: unknown; message?: unknown };
      if (typeof body.error === 'string') return body.error;
      if (typeof body.message === 'string') return body.message;
    } catch {
      // Preserve the standard error message if the function did not return JSON.
    }
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

function formatCashierActivityAnswer(staffName: string, matches: CashierActivityInvestigation[]) {
  if (matches.length === 0) {
    return `I could not find a staff member named “${staffName}” in this organization. Try their full first and last name.`;
  }
  if (matches.length > 1) {
    return `I found multiple staff members matching “${staffName}”: ${matches.map((match) => match.staff_name).join(', ')}. Please use the full name so I can report the correct POS activity.`;
  }

  const result = matches[0];
  const range = result.evidence?.date_range;
  const actionBreakdown = Object.entries(result.audit_actions || {})
    .map(([action, count]) => `${action.replace(/_/g, ' ')}: ${count}`)
    .join(', ') || 'no audited events';
  const operationalFacts = `${result.staff_name} recorded ${Number(result.audit_event_count || 0)} audited actions (${actionBreakdown}) between ${formatDate(range?.start_at || null)} and ${formatDate(range?.end_at || null)}. They created ${Number(result.orders_created || 0)} orders, opened ${Number(result.register_sessions_opened || 0)} register sessions, and recorded ${Number(result.cash_drawer_transaction_count || 0)} cash-drawer transactions.`;
  const financialFacts = result.financial_metrics_available
    ? ` They processed ${Number(result.payments_count || 0)} completed payments totaling ${formatAmount(result.payments_amount || 0)} and ${Number(result.refunds_count || 0)} refunds totaling ${formatAmount(result.refunds_amount || 0)} (organization currency).`
    : ' Payment and refund amounts are withheld because your role does not include sales-report access.';
  const timing = result.first_activity_at || result.last_activity_at
    ? ` First recorded activity: ${formatDate(result.first_activity_at)}. Most recent: ${formatDate(result.last_activity_at)}.`
    : '';
  return `${operationalFacts}${financialFacts}${timing}\n\nEvidence: audit logs for the action trail, plus orders, payments, refunds, register sessions, and cash-drawer transactions for POS facts. Customer and order details are not exposed in this summary.`;
}

function formatSalesRankingAnswer(rows: SalesProductRanking[], startAt: Date, endAt: Date) {
  const range = `${startAt.toLocaleDateString()} to ${endAt.toLocaleDateString()}`;
  if (rows.length === 0) return `I found no completed POS item sales from ${range} in your accessible location scope.`;
  const top = rows[0];
  const topName = top.sku ? `${top.product_name} (${top.sku})` : top.product_name;
  const runnersUp = rows.slice(1, 5).map((item, index) => `${index + 2}. ${item.sku ? `${item.product_name} (${item.sku})` : item.product_name}: ${Number(item.units_sold).toLocaleString()} units`).join('\n');
  return `Your most-sold product from ${range} was ${topName}: ${Number(top.units_sold).toLocaleString()} units across ${Number(top.order_count).toLocaleString()} completed orders, generating ${formatAmount(top.net_sales)} in item sales.${runnersUp ? `\n\nNext best sellers:\n${runnersUp}` : ''}\n\nEvidence: completed, ready, and sent POS orders in your authorized scope; voided line items are excluded.`;
}

export function PurchasingAssistantBubble() {
  const { organization, profile } = useAuth();
  const { hasPermission } = usePermissions();
  const { currentLocation, locationScope } = useLocationStore();
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [aiSettings, setAiSettings] = useState<AiAssistantSettings | null>(null);
  const [aiSettingsLoaded, setAiSettingsLoaded] = useState(false);
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const [providerMode, setProviderMode] = useState<'platform_openai' | 'customer_openai' | 'customer_anthropic'>('platform_openai');
  const [customerApiKey, setCustomerApiKey] = useState('');
  const [setupError, setSetupError] = useState<string | null>(null);
  const [isSavingSetup, setIsSavingSetup] = useState(false);

  const purchasingManagerEnabled = isPurchasingManagerEnabled(organization?.settings, profile?.role);
  const hasPurchasingAccess = hasPermission('inventory.purchasing.view') || hasPermission('inventory.purchase_orders.view');
  const hasAuditAccess = hasPermission('audit.view');
  const hasSalesAccess = hasPermission('reports.view_sales');
  const hasInventoryAccess = hasPermission('reports.view_inventory')
    || hasPermission('reports.view_inventory_reports')
    || hasPermission('inventory.view')
    || hasPermission('inventory.view_stock');
  const hasAccess = hasPurchasingAccess || hasAuditAccess || hasSalesAccess || hasInventoryAccess;
  const canConfigureAi = ['superadmin', 'primary_admin', 'owner', 'admin'].includes(profile?.role || '');
  const starterQuestions = [
    ...(hasPurchasingAccess ? STARTER_QUESTIONS : []),
    ...(hasSalesAccess ? ['What was my most sold product this year?'] : []),
    ...(hasInventoryAccess ? ['What inventory was received, wasted, or adjusted this month?'] : []),
    ...(hasAuditAccess ? ['How many times did staff Alex Johnson log in?', 'Show cashier Alex Johnson’s POS activity.'] : []),
  ];

  useEffect(() => {
    let active = true;
    if (!organization?.id) return undefined;
    setAiSettingsLoaded(false);
    void supabase.rpc('get_ai_assistant_settings').then(({ data }) => {
      if (!active) return;
      const settings = ((data || []) as AiAssistantSettings[])[0] || null;
      setAiSettings(settings);
      setProviderMode(settings?.provider_mode || 'platform_openai');
      setAiSettingsLoaded(true);
    });
    return () => { active = false; };
  }, [organization?.id]);

  if (!purchasingManagerEnabled || !hasAccess) return null;

  const completeAiSetup = async () => {
    if (!canConfigureAi) {
      setSetupError('An organization administrator must choose the AI provider.');
      return;
    }
    setSetupError(null);
    setIsSavingSetup(true);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session?.access_token) throw new Error('Your session has expired. Please sign in again before configuring AI access.');
      const { data, error } = await supabase.functions.invoke('configure-ai-assistant', {
        body: { providerMode, apiKey: providerMode === 'platform_openai' ? undefined : customerApiKey },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw new Error(await functionErrorMessage(error, 'AI setup could not be saved.'));
      if (data?.error) throw new Error(data.error);
      const { data: refreshed } = await supabase.rpc('get_ai_assistant_settings');
      setAiSettings(((refreshed || []) as AiAssistantSettings[])[0] || null);
      setCustomerApiKey('');
      setIsSetupOpen(false);
      setIsOpen(true);
    } catch (error) {
      setSetupError(error instanceof Error ? error.message : 'AI setup could not be saved.');
    } finally {
      setIsSavingSetup(false);
    }
  };

  const openProviderSettings = () => {
    setProviderMode(aiSettings?.provider_mode || 'platform_openai');
    setCustomerApiKey('');
    setSetupError(null);
    setIsSetupOpen(true);
  };

  const getFallbackAnswer = async (nextQuestion: string) => {
    if (!organization?.id) throw new Error('Organization context is unavailable.');

    if (isStaffLoginInvestigation(nextQuestion)) {
      if (!hasAuditAccess) throw new Error('You do not have permission to investigate staff audit logs.');
      const staffName = getStaffNameFromQuestion(nextQuestion);
      if (!staffName) throw new Error('Tell me the staff member’s name so I can investigate their login activity.');

      const endAt = new Date();
      const startAt = new Date(endAt);
      startAt.setDate(startAt.getDate() - 30);
      const { data, error } = await supabase.rpc('investigate_staff_logins', {
        p_organization_id: organization.id,
        p_staff_query: staffName,
        p_start_at: startAt.toISOString(),
        p_end_at: endAt.toISOString(),
      });
      if (error) throw error;
      const matches = (data || []) as StaffLoginInvestigation[];
      if (matches.length === 0) {
        return `I could not find a staff member named “${staffName}” in this organization. Try their full first and last name.`;
      }
      if (matches.length > 1) {
        return `I found multiple staff members matching “${staffName}”: ${matches.map((match) => match.staff_name).join(', ')}. Please use the full name so I can report the correct login count.`;
      }
      const result = matches[0];
      const count = Number(result.login_count || 0);
      const range = result.evidence?.date_range;
      return `${result.staff_name} has ${count} recorded ${count === 1 ? 'login' : 'logins'} between ${formatDate(range?.start_at || null)} and ${formatDate(range?.end_at || null)}. ${count > 0 ? `First: ${formatDate(result.first_login_at)}. Most recent: ${formatDate(result.last_login_at)}.` : 'No explicit login event was recorded in this period.'}\n\nEvidence: audit_logs action “login” only. Session refreshes and heartbeat events are not counted.`;
    }

    if (isCashierActivityInvestigation(nextQuestion)) {
      if (!hasAuditAccess) throw new Error('You do not have permission to investigate staff audit logs.');
      const staffName = getStaffNameFromActivityQuestion(nextQuestion);
      if (!staffName) throw new Error('Tell me the cashier’s name so I can investigate their POS activity.');

      const endAt = new Date();
      const startAt = new Date(endAt);
      startAt.setDate(startAt.getDate() - 30);
      const { data, error } = await supabase.rpc('investigate_cashier_activity', {
        p_organization_id: organization.id,
        p_staff_query: staffName,
        p_start_at: startAt.toISOString(),
        p_end_at: endAt.toISOString(),
      });
      if (error) throw error;
      return formatCashierActivityAnswer(staffName, (data || []) as CashierActivityInvestigation[]);
    }

    if (isSalesQuestion(nextQuestion)) {
      if (!hasSalesAccess) throw new Error('You do not have permission to view sales reports.');
      const { startAt, endAt } = getQuestionDateRange(nextQuestion);
      const { data, error } = await supabase.rpc('get_assistant_sales_product_ranking', {
        p_organization_id: organization.id,
        p_location_id: locationScope === 'single' ? currentLocation?.id || null : null,
        p_start_at: startAt.toISOString(),
        p_end_at: endAt.toISOString(),
        p_limit: 10,
      });
      if (error) throw error;
      return formatSalesRankingAnswer((data || []) as SalesProductRanking[], startAt, endAt);
    }

    if (!hasPurchasingAccess) throw new Error('You do not have permission to view purchasing data.');
    const { data, error } = await supabase.rpc('get_purchasing_manager_recommendations', {
      p_organization_id: organization.id,
      p_location_id: locationScope === 'single' ? currentLocation?.id || null : null,
      p_supplier_id: null,
    });
    if (error) throw error;
    const items = ((data || []) as FallbackRecommendation[])
      .filter((item) => item.priority === 'urgent' || item.priority === 'high')
      .slice(0, 3);
    if (items.length === 0) {
      return 'I found no urgent replenishment recommendations in the current scope. Review items with missing policies before relying on a forecast.';
    }
    return `The live purchasing summary is: ${items.map((item) => `${item.item_name} at ${item.location_name}: order ${Number(item.recommended_quantity)} ${item.unit_of_measure} (${item.confidence} confidence). ${item.reason}`).join(' ')}`;
  };

  const sendQuestion = async (event?: FormEvent, preset?: string) => {
    event?.preventDefault();
    const nextQuestion = (preset || question).trim();
    if (!nextQuestion || isSending) return;
    if (!aiSettings) {
      setIsSetupOpen(true);
      return;
    }

    setMessages((current) => [...current, { role: 'user', content: nextQuestion }]);
    setQuestion('');
    setIsSending(true);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session?.access_token) throw new Error('Your session has expired. Please sign in again before using Operations Assistant.');
      const { data, error } = await supabase.functions.invoke('purchasing-assistant', {
        body: {
          question: nextQuestion,
          locationId: locationScope === 'single' ? currentLocation?.id || null : null,
          history: messages.slice(-6).map((message) => ({ role: message.role, content: message.content })),
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setMessages((current) => [...current, {
        role: 'assistant',
        content: data?.answer || 'I could not find an answer in the current purchasing data.',
        asOf: data?.asOf,
      }]);
    } catch (error) {
      try {
        const fallback = await getFallbackAnswer(nextQuestion);
        setMessages((current) => [...current, {
          role: 'assistant',
          content: `${fallback}\n\nThe full conversational assistant is temporarily unavailable, so this answer is a live, deterministic result from your authorized data.`,
          asOf: new Date().toISOString(),
        }]);
        return;
      } catch {
        // Preserve the original endpoint error when the scoped fallback cannot load either.
      }
      setMessages((current) => [...current, {
        role: 'assistant',
        content: error instanceof Error ? error.message : 'Purchasing Manager could not respond. Please try again.',
      }]);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
      <Dialog open={isSetupOpen} onOpenChange={setIsSetupOpen}>
        <DialogContent className="max-w-xl" hideCloseButton={!aiSettings?.can_configure && profile?.role !== 'superadmin'}>
          <DialogHeader>
            <DialogTitle>{aiSettings ? 'Change AI provider' : 'Set up Operations Assistant'}</DialogTitle>
            <DialogDescription>{aiSettings ? 'Choose a new provider for this organization. The change takes effect for new chats.' : 'Choose how this organization connects to AI before anyone starts a chat.'}</DialogDescription>
          </DialogHeader>
          {canConfigureAi ? (
            <div className="space-y-4">
              <button type="button" onClick={() => setProviderMode('platform_openai')} className={`w-full rounded-xl border p-4 text-left transition-colors ${providerMode === 'platform_openai' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}>
                <p className="font-medium">Use BlazePOS AI</p>
                <p className="mt-1 text-sm text-muted-foreground">OpenAI is provided by BlazePOS. Usage is metered and billed to this organization.</p>
              </button>
              <button type="button" onClick={() => setProviderMode('customer_openai')} className={`w-full rounded-xl border p-4 text-left transition-colors ${providerMode === 'customer_openai' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}>
                <p className="font-medium">Use your own OpenAI key</p>
                <p className="mt-1 text-sm text-muted-foreground">Your OpenAI account is billed directly. BlazePOS encrypts the key and never displays it again.</p>
              </button>
              <button type="button" onClick={() => setProviderMode('customer_anthropic')} className={`w-full rounded-xl border p-4 text-left transition-colors ${providerMode === 'customer_anthropic' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}>
                <p className="font-medium">Use your own Claude key</p>
                <p className="mt-1 text-sm text-muted-foreground">Your Anthropic account is billed directly. BlazePOS encrypts the key and never displays it again.</p>
              </button>
              {providerMode !== 'platform_openai' && <div className="space-y-2"><Label htmlFor="customer-ai-key">{providerMode === 'customer_anthropic' ? 'Anthropic API key' : 'OpenAI API key'}</Label><Input id="customer-ai-key" type="password" value={customerApiKey} onChange={(event) => setCustomerApiKey(event.target.value)} placeholder={providerMode === 'customer_anthropic' ? 'sk-ant-...' : 'sk-...'} autoComplete="off" /><p className="text-xs text-muted-foreground">Need a key? <a className="font-medium text-primary underline-offset-4 hover:underline" href={providerMode === 'customer_anthropic' ? 'https://platform.claude.com/settings/keys' : 'https://platform.openai.com/api-keys'} target="_blank" rel="noreferrer">Create one in {providerMode === 'customer_anthropic' ? 'Claude' : 'OpenAI'}</a>.</p></div>}
              {setupError && <p className="text-sm text-destructive">{setupError}</p>}
              <DialogFooter><Button onClick={() => void completeAiSetup()} disabled={isSavingSetup || (providerMode !== 'platform_openai' && !customerApiKey.trim())}>{isSavingSetup ? 'Saving…' : 'Continue to chat'}</Button></DialogFooter>
            </div>
          ) : <p className="text-sm text-muted-foreground">An organization administrator must choose the AI provider before chat is available.</p>}
        </DialogContent>
      </Dialog>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.section
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
            aria-label="Operations Assistant chat"
            className="flex h-[min(620px,calc(100vh-7rem))] w-[min(420px,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-2xl bg-background shadow-[0_18px_45px_rgba(15,23,42,0.18),0_3px_12px_rgba(15,23,42,0.08)] ring-1 ring-black/10 dark:ring-white/10"
          >
            <div className="flex items-start justify-between gap-3 border-b bg-primary px-4 py-3.5 text-primary-foreground">
              <div className="flex gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15">
                  <Bot className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-semibold leading-5 text-wrap-balance">Operations Assistant</h2>
                  <p className="mt-0.5 text-xs text-primary-foreground/80">Evidence-backed sales, inventory, and staff insights</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {canConfigureAi && <Button variant="ghost" size="sm" className="h-9 px-2 text-xs text-primary-foreground hover:bg-white/15 hover:text-primary-foreground" onClick={openProviderSettings}>Change provider</Button>}
                <Button aria-label="Close Operations Assistant chat" variant="ghost" size="icon" className="h-10 w-10 shrink-0 text-primary-foreground hover:bg-white/15 hover:text-primary-foreground active:scale-[0.96]" onClick={() => setIsOpen(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto bg-muted/20 p-4">
              {messages.length === 0 ? (
                <div className="space-y-4 pt-2">
                  <div className="rounded-xl bg-background p-4 shadow-sm ring-1 ring-black/5 dark:ring-white/10">
                    <p className="text-sm font-medium">Ask about sales, stock, purchasing patterns, supplier spend, or staff activity.</p>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">Answers use only data you are permitted to access and include the evidence used.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {starterQuestions.map((starter) => (
                      <Button key={starter} size="sm" variant="outline" className="h-auto rounded-lg px-3 py-2 text-left text-xs active:scale-[0.96]" onClick={() => void sendQuestion(undefined, starter)}>
                        {starter}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : messages.map((message, index) => (
                <motion.div
                  key={`${message.role}-${index}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18 }}
                  className={message.role === 'user' ? 'ml-10 rounded-2xl rounded-br-md bg-primary px-3.5 py-2.5 text-sm text-primary-foreground' : 'mr-4 rounded-2xl rounded-bl-md bg-background px-3.5 py-2.5 text-sm leading-6 shadow-sm ring-1 ring-black/5 dark:ring-white/10'}
                >
                  <p className="whitespace-pre-wrap">{message.content}</p>
                  {message.asOf && <p className="mt-2 text-[11px] text-muted-foreground">As of {new Date(message.asOf).toLocaleString()}</p>}
                </motion.div>
              ))}
              {isSending && <div className="mr-4 flex items-center gap-2 rounded-2xl rounded-bl-md bg-background px-3.5 py-2.5 text-xs text-muted-foreground shadow-sm ring-1 ring-black/5 dark:ring-white/10"><Sparkles className="h-3.5 w-3.5 animate-pulse text-primary" />Reviewing authorized evidence…</div>}
            </div>

            <form onSubmit={(event) => void sendQuestion(event)} className="border-t bg-background p-3">
              <div className="flex items-center gap-2">
                <Input value={question} onChange={(event) => setQuestion(event.target.value)} disabled={isSending} placeholder="Ask about sales, inventory, or staff activity…" className="h-11 rounded-xl" maxLength={1200} />
                <Button aria-label="Send assistant question" type="submit" size="icon" disabled={!question.trim() || isSending} className="h-11 w-11 shrink-0 rounded-xl active:scale-[0.96]">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="px-1 pt-2 text-[11px] text-muted-foreground">{locationScope === 'single' && currentLocation ? `Using ${currentLocation.name}` : 'Using all accessible locations'}</p>
            </form>
          </motion.section>
        )}
      </AnimatePresence>

      <Button
        aria-label={isOpen ? 'Close Operations Assistant chat' : 'Open Operations Assistant chat'}
        aria-expanded={isOpen}
        size="icon"
        className="h-14 w-14 rounded-2xl shadow-[0_10px_25px_rgba(15,23,42,0.24),0_2px_8px_rgba(15,23,42,0.12)] transition-transform active:scale-[0.96]"
        onClick={() => {
          if (!aiSettingsLoaded || aiSettings) setIsOpen((open) => !open);
          else setIsSetupOpen(true);
        }}
      >
        <span className="relative flex h-6 w-6 items-center justify-center">
          <MessageCircle className={`absolute h-6 w-6 transition-[opacity,transform,filter] duration-200 ${isOpen ? 'scale-[0.25] opacity-0 blur-[4px]' : 'scale-100 opacity-100 blur-0'}`} />
          <X className={`absolute h-5 w-5 transition-[opacity,transform,filter] duration-200 ${isOpen ? 'scale-100 opacity-100 blur-0' : 'scale-[0.25] opacity-0 blur-[4px]'}`} />
        </span>
      </Button>
    </div>
  );
}
